import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/**
 * State Record admin panel (ADR-033 Part D).
 * Route: /admin/states
 *
 * Melio-only view/edit surface for the GLOBAL states table: Phase-1 fields
 * only, confidence gate, and manual version bump. Every save goes through
 * the vendor admin API, which bumps `version` and writes an org-less
 * authorization record + audit entry; non-admins get a 403 from the API
 * (same gate as the rest of /admin).
 *
 * ADR-034 fields (wage tax, assessment ratios, taxability, recapture) are
 * now editable. Still-dormant later-ADR columns (withholding, tier presence)
 * remain absent; the API rejects them anyway.
 */

type StateRecord = {
  postal: string;
  name: string;
  fips: string;
  version: number;
  confidence: 'estimated' | 'reviewed' | 'verified';
  lastReviewed: string | null;
  hasLocalSalesTax: boolean;
  defaultCitySalesTaxRate: string | null;
  salesTaxSourcing: 'origin' | 'destination' | null;
  hasHotelOccupancyTax: boolean;
  defaultHotRate: string | null;
  hotStatutoryCap: string | null;
  localExciseShare: string | null;
  defaultCityPropertyTaxRate: string | null;
  defaultCountyPropertyTaxRate: string | null;
  defaultSchoolPropertyTaxRate: string | null;
  defaultSpecialPropertyTaxRate: string | null;
  defaultCityCostPerWorker: string | null;
  defaultCountyCostPerWorker: string | null;
  defaultIsdCostPerStudent: string | null;
  defaultMiscTaxesFeesPerWorker: string | null;
  defaultHouseholdTaxableSpendingPerWorker: string | null;
  defaultUtilityFranchiseFeePerJob: string | null;
  defaultCostInflationRate: string | null;
  assessmentRatioReal: string | null;
  assessmentRatioBpp: string | null;
  assessmentRatioInventory: string | null;
  defaultLocalWageTaxRate: string | null;
  residentTaxableShareDefault: string | null;
  schoolLocalRetentionShare: string | null;
  defaultWithholdingRate: string | null;
  countyLabel: string;
  schoolDistrictLabel: string;
  updatedAt: string;
};

const NUMERIC_FIELDS: Array<{ key: keyof StateRecord; label: string; group: string }> = [
  { key: 'defaultCitySalesTaxRate', label: 'City sales tax rate', group: 'Sales & excise' },
  { key: 'defaultHotRate', label: 'Hotel occupancy tax rate', group: 'Sales & excise' },
  { key: 'hotStatutoryCap', label: 'HOT statutory cap', group: 'Sales & excise' },
  { key: 'localExciseShare', label: 'Local excise share (eff. rate)', group: 'Sales & excise' },
  { key: 'defaultCityPropertyTaxRate', label: 'City property tax rate', group: 'Property' },
  { key: 'defaultCountyPropertyTaxRate', label: 'County property tax rate', group: 'Property' },
  { key: 'defaultSchoolPropertyTaxRate', label: 'School property tax rate', group: 'Property' },
  { key: 'defaultSpecialPropertyTaxRate', label: 'Special district rate', group: 'Property' },
  { key: 'defaultCityCostPerWorker', label: 'City cost / worker', group: 'Cost of services' },
  { key: 'defaultCountyCostPerWorker', label: 'County cost / worker', group: 'Cost of services' },
  { key: 'defaultIsdCostPerStudent', label: 'School cost / student', group: 'Cost of services' },
  {
    key: 'defaultMiscTaxesFeesPerWorker',
    label: 'Misc taxes/fees / worker',
    group: 'Cost of services',
  },
  {
    key: 'defaultHouseholdTaxableSpendingPerWorker',
    label: 'Household taxable spend / worker',
    group: 'Cost of services',
  },
  {
    key: 'defaultUtilityFranchiseFeePerJob',
    label: 'Utility franchise fee / job',
    group: 'Cost of services',
  },
  { key: 'defaultCostInflationRate', label: 'Cost inflation rate', group: 'Cost of services' },
  // ADR-034 2.4: assessed = market x ratio. 1.0 = assessed equals market (TX).
  { key: 'assessmentRatioReal', label: 'Real property ratio', group: 'Assessment' },
  { key: 'assessmentRatioBpp', label: 'BPP ratio', group: 'Assessment' },
  { key: 'assessmentRatioInventory', label: 'Inventory ratio', group: 'Assessment' },
  // ADR-034 2.1: local wage tax.
  { key: 'defaultLocalWageTaxRate', label: 'Local wage tax rate', group: 'Wage tax' },
  { key: 'residentTaxableShareDefault', label: 'Resident taxable share', group: 'Wage tax' },
  // ADR-034 2.3: school recapture.
  {
    key: 'schoolLocalRetentionShare',
    label: 'ISD local retention share',
    group: 'School recapture',
  },
  // ADR-035 3.3: withholding-grant base.
  { key: 'defaultWithholdingRate', label: 'Default withholding rate', group: 'Grants' },
];

function ConfidenceBadge({ confidence }: { confidence: StateRecord['confidence'] }) {
  const map = {
    estimated: 'bg-amber-50 text-amber-700 border-amber-200',
    reviewed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    verified: 'bg-blue-50 text-blue-700 border-blue-200',
  } as const;
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${map[confidence]}`}
    >
      {confidence}
    </span>
  );
}

export function AdminStatesRoute() {
  const getToken = useSessionTokenGetter();
  const [records, setRecords] = useState<StateRecord[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const authedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const token = await getToken();
      const res = await fetch(`${API}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(init?.headers ?? {}),
        },
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `${res.status}`);
      }
      return res.json();
    },
    [getToken],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await authedFetch('/api/v1/admin/states')) as { states: StateRecord[] };
      setRecords(data.states);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = records.find((r) => r.postal === selected) ?? null;

  function beginEdit(record: StateRecord) {
    setSelected(record.postal);
    setNotice(null);
    const next: Record<string, string> = {
      countyLabel: record.countyLabel,
      schoolDistrictLabel: record.schoolDistrictLabel,
    };
    for (const f of NUMERIC_FIELDS) {
      next[f.key] = (record[f.key] as string | null) ?? '';
    }
    setForm(next);
  }

  async function save() {
    if (!current) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // Send only fields that differ from the loaded record; empty string
      // means "clear to null / inherit".
      const payload: Record<string, unknown> = {};
      for (const f of NUMERIC_FIELDS) {
        const was = (current[f.key] as string | null) ?? '';
        const now = form[f.key] ?? '';
        if (now !== was) payload[f.key] = now === '' ? null : Number(now);
      }
      for (const key of ['countyLabel', 'schoolDistrictLabel'] as const) {
        if (form[key] !== current[key]) payload[key] = form[key];
      }
      if (Object.keys(payload).length === 0) {
        setNotice('No changes to save.');
        return;
      }
      const res = (await authedFetch(`/api/v1/admin/states/${current.postal}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })) as { state: StateRecord; authorizationRecordId: string };
      setNotice(
        `Saved. Version ${res.state.version}; authorization record ${res.authorizationRecordId}.`,
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function setConfidence(confidence: StateRecord['confidence']) {
    if (!current) return;
    setSaving(true);
    setError(null);
    try {
      const res = (await authedFetch(`/api/v1/admin/states/${current.postal}/set-confidence`, {
        method: 'POST',
        body: JSON.stringify({ confidence }),
      })) as { state: StateRecord };
      setNotice(`Confidence set to ${confidence}; version ${res.state.version}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function bumpVersion() {
    if (!current) return;
    setSaving(true);
    setError(null);
    try {
      const res = (await authedFetch(`/api/v1/admin/states/${current.postal}/bump-version`, {
        method: 'POST',
      })) as { state: StateRecord };
      setNotice(`Version bumped to ${res.state.version}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const groups = [...new Set(NUMERIC_FIELDS.map((f) => f.group))];

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="mb-6">
        <Link to="/admin" className="text-xs text-slate-400 hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">State Records</h1>
        <p className="mt-1 text-sm text-slate-500">
          Global, Melio-curated per-state configuration (ADR-033). Edits bump the record version and
          write an authorization record; analyses stamp the version they were computed against.
          States at "estimated" show a banner on customer reports.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-6 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-5 gap-6">
          {/* State list */}
          <div className="col-span-2 rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Confidence</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Last reviewed</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.postal}
                    onClick={() => beginEdit(r)}
                    className={`cursor-pointer border-b border-slate-50 hover:bg-slate-50 ${
                      selected === r.postal ? 'bg-brand-teal-50/50' : ''
                    }`}
                  >
                    <td className="px-3 py-2 font-semibold text-slate-700">
                      {r.postal}
                      <span className="ml-2 font-normal text-slate-400">{r.name}</span>
                    </td>
                    <td className="px-3 py-2">
                      <ConfidenceBadge confidence={r.confidence} />
                    </td>
                    <td className="px-3 py-2 text-slate-500">v{r.version}</td>
                    <td className="px-3 py-2 text-slate-500">{r.lastReviewed ?? '·'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Editor */}
          <div className="col-span-3">
            {!current ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
                Select a state to view and edit its record.
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {current.name} ({current.postal})
                    </h2>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <ConfidenceBadge confidence={current.confidence} />
                      <span>v{current.version}</span>
                      <span>FIPS {current.fips}</span>
                      {current.lastReviewed && <span>reviewed {current.lastReviewed}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(['estimated', 'reviewed', 'verified'] as const)
                      .filter((c) => c !== current.confidence)
                      .map((c) => (
                        <button
                          key={c}
                          type="button"
                          disabled={saving}
                          onClick={() => void setConfidence(c)}
                          className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Mark {c}
                        </button>
                      ))}
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void bumpVersion()}
                      className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Bump version
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="text-xs">
                    <span className="mb-1 block font-medium text-slate-600">County label</span>
                    <input
                      value={form.countyLabel ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, countyLabel: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs">
                    <span className="mb-1 block font-medium text-slate-600">
                      School district label
                    </span>
                    <input
                      value={form.schoolDistrictLabel ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, schoolDistrictLabel: e.target.value }))
                      }
                      className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs"
                    />
                  </label>
                </div>

                {groups.map((group) => (
                  <div key={group} className="mt-5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {group}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      {NUMERIC_FIELDS.filter((f) => f.group === group).map((f) => (
                        <label key={f.key} className="text-xs">
                          <span className="mb-1 block font-medium text-slate-600">{f.label}</span>
                          <input
                            type="number"
                            step="0.000001"
                            value={form[f.key] ?? ''}
                            placeholder="inherit US default"
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                            }
                            className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <p className="text-[10px] text-slate-400">
                    Empty numeric fields inherit the US hard defaults. Saving bumps the version and
                    writes an authorization record. Dormant ADR-033 fields (income, assessment
                    ratios, taxability, recapture, tier presence) are not editable until their ADR
                    lands.
                  </p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void save()}
                    className="ml-4 flex-shrink-0 rounded bg-brand-teal-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-teal-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
